import type { AerialPlotResponse, Message } from "./types";
import type {
	MeanComparisonOptionsData,
	ProtocolMeanComparisonData,
	ProtocolMeanComparisonRequestRow,
	TimecourseAnovaType,
	TimecourseDataViewModel,
	TimecourseMeanComparisonData,
} from "./types/statisticsTypes";

/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */
// @ts-expect-error -- process.env.aerialPlotConnections is injected by webpack and isn't typed
const statisticsApiUri: string = process.env.aerialPlotConnections.aerialStatisticsApi.baseUrl;
/* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access */

/** Legacy, used by the redux statistics actions. Prefer {@link getMeanComparisonForAucAsync}. */
export function getMeanComparisonForAuc(
	aucData: unknown,
	clientId: string,
	meanComparisonId: string,
	alphaId: string,
	isDataUnbalanced: boolean,
	accessToken: string,
): Promise<unknown> {
	return fetch(
		`${statisticsApiUri}api/v1/MeanComparison/GetMeanComparisonLettersForAuc?clientId=${clientId}&meanComparisonId=${meanComparisonId}&alphaId=${alphaId}&unbalancedData=${isDataUnbalanced}`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(aucData),
		},
	)
		.then((res) => res.json() as Promise<AerialPlotResponse<unknown>>)
		.then((data) => data.data);
}

/** Legacy, used by the redux statistics actions. Prefer {@link getMeanComparisonForTimecourseAsync}. */
export function getMeanComparisonForTimecourse(
	timecourseData: TimecourseDataViewModel,
	clientId: string,
	analysisId: string,
	meanComparisonId: string,
	alphaId: string,
	accessToken: string,
	removeDamagedOrExcluded: boolean,
	removeOutliers: boolean,
	outlierType: number,
): Promise<AerialPlotResponse<TimecourseMeanComparisonData | string, Message[]>> {
	return fetch(
		`${statisticsApiUri}api/v1/MeanComparison/GetMeanComparisonLettersForTimecourse?clientId=${clientId}&analysisId=${analysisId}&meanComparisonId=${meanComparisonId}&alphaId=${alphaId}
			&removeDamagedOrExcluded=${removeDamagedOrExcluded}&removeOutliers=${removeOutliers}&outlierType=${outlierType}`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(timecourseData),
		},
	).then((res) => res.json() as Promise<AerialPlotResponse<TimecourseMeanComparisonData | string, Message[]>>);
}

/**
 * POST api/v1/MeanComparison/GetProtocolMeanComparisonData. Returns the whole response: `data` is
 * null when the server rejects the input (e.g. all values zero), still with HTTP 200.
 */
export async function getProtocolTukeyDataAsync(
	protocolData: ProtocolMeanComparisonRequestRow[],
	alphaId: string,
	meanComparisonId: string,
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<ProtocolMeanComparisonData | null, Message[] | null>> {
	const tukeyData: { protocolData: ProtocolMeanComparisonRequestRow[] } = { protocolData: [] };

	for (const pd of protocolData) {
		tukeyData.protocolData.push({ ...pd, alphaId, meanComparisonId });
	}

	const res = await fetch(
		`${statisticsApiUri}api/v1/MeanComparison/GetProtocolMeanComparisonData?meanComparisonId=${meanComparisonId}&clientId=${clientId}&alphaId=${alphaId}`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(tukeyData),
		},
	);

	if (!res.ok) {
		throw new Error("failed to fetch protocol tukey data");
	}

	const data = (await res.json()) as AerialPlotResponse<ProtocolMeanComparisonData | null, Message[] | null>;
	return data;
}

/** GET api/v1/MeanComparison/GetMeanComparisonsOptions. `data` is a message string when unauthorized. */
export function getMeanComparisonsOptions(
	clientId: string,
	accessToken: string,
): Promise<AerialPlotResponse<MeanComparisonOptionsData | string, Message[]>> {
	return fetch(`${statisticsApiUri}api/v1/MeanComparison/GetMeanComparisonsOptions?clientId=${clientId}`, {
		method: "GET",
		headers: {
			Authorization: `Bearer ${accessToken}`,
		},
	}).then((res) => res.json() as Promise<AerialPlotResponse<MeanComparisonOptionsData | string, Message[]>>);
}

/**
 * POST api/v1/MeanComparison/GetMeanComparisonLettersForAuc. Returns `data` only. Body and response
 * are untyped; callers assert them.
 */
export async function getMeanComparisonForAucAsync(
	aucData: unknown,
	clientId: string,
	meanComparisonId: string,
	alphaId: string,
	isDataUnbalanced: boolean,
	accessToken: string,
): Promise<unknown> {
	const res = await fetch(
		`${statisticsApiUri}api/v1/MeanComparison/GetMeanComparisonLettersForAuc?clientId=${clientId}&meanComparisonId=${meanComparisonId}&alphaId=${alphaId}&unbalancedData=${isDataUnbalanced}`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(aucData),
		},
	);

	if (!res.ok) {
		throw new Error("failed to get mean comparisons for aucs");
	}

	const data = (await res.json()) as AerialPlotResponse<unknown>;
	return data.data;
}

/**
 * POST api/v1/MeanComparison/GetMeanComparisonLettersForTimecourse. Returns the whole response:
 * on a bad argument (too few treatments or flights, an invalid anovaType) `statusCode` is 400 and
 * `data` is the reason string, still with HTTP 200.
 */
export async function getMeanComparisonForTimecourseAsync(
	timecourseData: TimecourseDataViewModel,
	clientId: string,
	analysisId: string,
	meanComparisonId: string,
	alphaId: string,
	accessToken: string,
	removeDamagedOrExcluded: boolean,
	removeOutliers: boolean,
	outlierType: number,
	anovaType: TimecourseAnovaType = "one-way",
	useGdus = false,
): Promise<AerialPlotResponse<TimecourseMeanComparisonData | string, Message[]>> {
	const res = await fetch(
		`${statisticsApiUri}api/v1/MeanComparison/GetMeanComparisonLettersForTimecourse?clientId=${clientId}&analysisId=${analysisId}&meanComparisonId=${meanComparisonId}&alphaId=${alphaId}
			&removeDamagedOrExcluded=${removeDamagedOrExcluded}&removeOutliers=${removeOutliers}&outlierType=${outlierType}&anovaType=${anovaType}&useGdus=${useGdus}`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${accessToken}`,
			},
			body: JSON.stringify(timecourseData),
		},
	);

	if (!res.ok) {
		throw new Error("failed to get mean comparisons for timecourse");
	}

	const data = (await res.json()) as AerialPlotResponse<TimecourseMeanComparisonData | string, Message[]>;
	return data;
}
